// Exact bidirectional conversion between the local ENU world frame and
// WGS84 lat/lng, matching the frame used by ReorientationPlugin in tiles.js
// (+X = west, +Z = north, +Y = up along the ellipsoid normal; origin at the
// departure city's lat/lng/elevation). Built from the same
// `ellipsoid.getObjectFrame(...).invert()` code path as the plugin, so world
// coordinates converted here agree with the streamed 3D tiles everywhere —
// no flat-earth drift hundreds of km from the origin.
import { Matrix4, Vector3, MathUtils } from 'three';
import { WGS84_ELLIPSOID, OBJECT_FRAME } from '3d-tiles-renderer';

const TWO_PI = Math.PI * 2;

// 角度差を (-π, π] に正規化する。方位の補間・整合判定で共通に使う。
export function angleDiff(a, b) {
  let d = a - b;
  while (d > Math.PI) d -= TWO_PI;
  while (d < -Math.PI) d += TWO_PI;
  return d;
}

// ENU 前計算済みの POI 配列（x/y/z を持つ）から最寄りを1件返す。
//   { item, distanceM(水平), bearingRad(0=北,+東) } / 空配列なら null
//
// ★ 順位付けは必ず 3D（弦）距離で行う。ENU の水平成分は R·sin(中心角) なので、
//   中心角 90° を超えると「遠いほど水平距離が小さくなる」向きに折り返し、地球の
//   裏側の POI が最寄りとして選ばれてしまう（例: サンパウロ出発で首里城が水平
//   641km ＝実際は 19,373km）。弦距離 2R·sin(中心角/2) は中心角に対して単調
//   増加なのでこの折り返しが起きない。表示に使う距離だけは従来どおり水平距離。
//
// ENU は +Z=北, +X=西 なので east = -X。方位 = atan2(east, north)。
export function nearestPoi(items, x, y, z) {
  let best = null;
  let bestSq = Infinity;
  for (const it of items) {
    const dx = it.x - x;
    const dy = it.y - y;
    const dz = it.z - z;
    const sq = dx * dx + dy * dy + dz * dz; // sqrt は勝者の1回だけにする
    if (sq < bestSq) {
      bestSq = sq;
      best = it;
    }
  }
  if (!best) return null;
  const dx = best.x - x;
  const dz = best.z - z;
  return {
    item: best,
    distanceM: Math.sqrt(dx * dx + dz * dz),
    bearingRad: Math.atan2(-dx, dz),
  };
}

export function createEnuFrame(originLatDeg, originLngDeg, originHeight = 0) {
  const enuToEcef = new Matrix4();
  WGS84_ELLIPSOID.getObjectFrame(
    MathUtils.degToRad(originLatDeg),
    MathUtils.degToRad(originLngDeg),
    originHeight,
    0, 0, 0,
    enuToEcef,
    OBJECT_FRAME
  );
  const ecefToEnu = enuToEcef.clone().invert();

  const _v = new Vector3();
  const _cart = { lat: 0, lon: 0, height: 0 };

  return {
    // lat/lng in degrees (+ optional ellipsoid height in metres) → ENU world
    // position (Vector3).
    latLngToWorld(latDeg, lngDeg, height = 0) {
      const p = new Vector3();
      WGS84_ELLIPSOID.getCartographicToPosition(
        MathUtils.degToRad(latDeg),
        MathUtils.degToRad(lngDeg),
        height,
        p
      );
      return p.applyMatrix4(ecefToEnu);
    },
    // ENU world position → { lat, lng } in degrees + ellipsoid height in
    // metres. Pass the real Y too: the ENU vertical diverges from the local
    // vertical with distance, so projecting from altitude matters (~300 m of
    // ground-track error at 10 km altitude 200 km out if Y were dropped).
    worldToLatLng(x, y, z) {
      _v.set(x, y, z).applyMatrix4(enuToEcef);
      WGS84_ELLIPSOID.getPositionToCartographic(_v, _cart);
      return {
        lat: MathUtils.radToDeg(_cart.lat),
        lng: MathUtils.radToDeg(_cart.lon),
        height: _cart.height,
      };
    },
  };
}
